/**
 * The account identifier ("Lender ID"): 10 lowercase alphanumerics, generated by the wizard —
 * never typed or shown (OM-3918). Derived from the Lender Name so the codes stay recognizable —
 * 'Acme Lending' -> 'acmelendin', zero-padded when short — and disambiguated with a numeric
 * tail when the derived code is already taken.
 */
export const LENDER_ID_LENGTH = 10;

export const deriveLenderId = (lenderName: string): string => {
  const base = lenderName.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!base) return '';
  return base.padEnd(LENDER_ID_LENGTH, '0').slice(0, LENDER_ID_LENGTH);
};

/** The nth fallback when the derived id collides: the tail is replaced by n. */
export const lenderIdVariant = (base: string, attempt: number): string => {
  const tail = String(attempt);
  return base.slice(0, LENDER_ID_LENGTH - tail.length) + tail;
};

/**
 * Whether a stored id is this name's derivation or one of its collision variants. A settled id
 * must not be re-probed: after a half-finished submit the lender already exists under it, and
 * probing again would step the id forward onto a second account.
 */
export const isDerivedFrom = (id: string, lenderName: string): boolean => {
  const base = deriveLenderId(lenderName);
  if (!base || !id) return false;
  if (id === base) return true;
  const tail = id.match(/[1-9]\d*$/)?.[0];
  return tail !== undefined && lenderIdVariant(base, Number(tail)) === id;
};
